"use client";

import { siteHref } from "../current";
import Link from "../link";
import { useRef, useState, useTransition } from "react";
import type { Result } from "../actions";
import { Badge, ConfirmButton, IconButton, Notice } from "./ui";

export type Row = { id: string; title: string; subtitle?: string; status: "published" | "draft"; editHref: string; viewHref?: string };

// A list in website order. Reorder by dragging the handle (mouse or finger) or with the ↑ ↓ buttons; the new order
// is saved straight away and is live on the website.
export function SortableList({ rows: initial, reorder, remove, empty }: {
  rows: Row[];
  reorder: (ids: string[]) => Promise<Result>;
  remove: (id: string) => Promise<Result>;
  empty: string;
}) {
  const [rows, setRows] = useState(initial);
  // Take fresh rows from the server whenever they change (after a save, delete or refresh elsewhere).
  const [shown, setShown] = useState(initial);
  if (shown !== initial) {
    setShown(initial);
    setRows(initial);
  }
  const [dragging, setDragging] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [pending, startTransition] = useTransition();
  const listRef = useRef<HTMLOListElement>(null);
  const dragOrder = useRef<Row[]>([]); // the order while a drag is in progress

  const persist = (next: Row[]) =>
    startTransition(async () => {
      const result = await reorder(next.map((r) => r.id));
      setNotice(result.ok ? { type: "ok", text: "Order saved. It's live on the website." } : { type: "error", text: result.error });
    });

  const move = (index: number, by: number) => {
    const next = [...rows];
    const [row] = next.splice(index, 1);
    next.splice(index + by, 0, row);
    setRows(next);
    persist(next);
  };

  // Pointer dragging: the row follows the pointer's position among the other rows' midpoints.
  const startDrag = (id: string, event: React.PointerEvent) => {
    event.preventDefault();
    setDragging(id);
    dragOrder.current = rows;
    const before = rows.map((r) => r.id).join();
    const onMove = (e: PointerEvent) => {
      const items = [...(listRef.current?.children ?? [])] as HTMLElement[];
      const current = dragOrder.current;
      const from = current.findIndex((r) => r.id === id);
      let to = items.findIndex((el) => e.clientY < el.getBoundingClientRect().top + el.offsetHeight / 2);
      if (to === -1) to = current.length - 1;
      else if (to > from) to -= 1;
      if (to !== from) {
        const next = [...current];
        const [row] = next.splice(from, 1);
        next.splice(to, 0, row);
        dragOrder.current = next;
        setRows(next);
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragging(null);
      if (dragOrder.current.map((r) => r.id).join() !== before) persist(dragOrder.current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const del = (id: string) =>
    startTransition(async () => {
      const result = await remove(id);
      if (result.ok) setRows((r) => r.filter((x) => x.id !== id));
      setNotice(result.ok ? { type: "ok", text: "Deleted." } : { type: "error", text: result.error });
    });

  if (!rows.length) return <p className="rounded-2xl border border-dashed border-midnight/20 p-8 text-center text-midnight/70">{empty}</p>;

  return (
    <div className="flex flex-col gap-3" aria-busy={pending}>
      <Notice notice={notice} />
      <p className="text-sm text-midnight/70">The order here is the order on the website. Drag the ⠿ handle, or use the arrows.</p>
      <ol ref={listRef} className="flex flex-col gap-2">
        {rows.map((row, i) => (
          <li
            key={row.id}
            className={`flex flex-wrap items-center gap-3 rounded-xl border bg-offwhite p-3 transition-shadow sm:flex-nowrap ${dragging === row.id ? "border-teal shadow-lg" : "border-midnight/10"}`}
          >
            <button
              type="button"
              aria-label={`Drag to reorder ${row.title}`}
              onPointerDown={(e) => startDrag(row.id, e)}
              className="grid size-11 shrink-0 cursor-grab touch-none place-items-center rounded-lg text-xl text-midnight/70 hover:bg-midnight/5 active:cursor-grabbing"
            >
              ⠿
            </button>
            <span className="w-6 shrink-0 font-mono text-xs text-midnight/70">{String(i + 1).padStart(2, "0")}</span>
            <div className="min-w-0 flex-1 basis-40">
              <Link href={row.editHref} className="block truncate py-1.5 font-bold hover:text-teal">
                {row.title}
              </Link>
              {row.subtitle && <p className="truncate text-sm text-midnight/70">{row.subtitle}</p>}
            </div>
            <Badge status={row.status} />
            <div className="flex items-center gap-1.5">
              <IconButton label="Move up" disabled={i === 0 || pending} onClick={() => move(i, -1)}>↑</IconButton>
              <IconButton label="Move down" disabled={i === rows.length - 1 || pending} onClick={() => move(i, 1)}>↓</IconButton>
              <Link href={row.editHref} className="grid min-h-11 place-items-center rounded-lg border border-midnight/15 px-3 font-mono text-[0.7rem] tracking-[0.15em] uppercase hover:border-teal hover:text-teal">
                Edit
              </Link>
              {row.viewHref && row.status === "published" && (
                <a href={siteHref(row.viewHref)} target="_blank" rel="noreferrer" aria-label={`View ${row.title} on the website`} className="grid size-11 place-items-center rounded-lg border border-midnight/15 hover:border-teal hover:text-teal">
                  ↗
                </a>
              )}
              <ConfirmButton label="✕" question="Delete?" onConfirm={() => del(row.id)} className="grid size-11 place-items-center rounded-lg border border-red-700/30 text-red-800 hover:bg-red-700 hover:text-white" />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
